import React, { useState, useContext, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
// Section for Utilities and Context
import "./layout.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

// Section for Components
import LoginModal from "../components/LoginModal";
import FeedbackModal from "../components/FeedbackModal";
import FeedbackDetailModal from "../components/FeedbackDetailModal";
import WorkspaceInviteModal from "../components/WorkspaceInviteModal";
import BillDigestModal from "../components/BillDigestModal";

const DIGEST_TYPES = ["bill_status_digest_personal", "bill_status_digest_workspace"];

export default function Layout() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const { auth, setAuth, isAdmin, contributor, isAmbassador } = useContext(AuthContext);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [showSidebar, setShowSidebar] = useState(false);
    const [showLoggedOutFeats, setShowLoggedOutFeats] = useState(false);
    const [showHomeIcon, setShowHomeIcon] = useState(false);
    const [awardsOpen, setAwardsOpen] = useState(false);
    const [adminUnreadCount, setAdminUnreadCount] = useState(0);
    const [notifUnreadCount, setNotifUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(null);
    const [billDigestModal, setBillDigestModal] = useState(null);
    const notifDropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isConfirmEmailPage = location.pathname.startsWith('/confirm-email');
    const isFeedbackPage = location.pathname.startsWith('/feedback');
    const isReportPage = location.pathname.startsWith('/report');

    // Track user activity on route changes
    useEffect(() => {
        if (auth.isAuthenticated && auth.user) {
            API.logUserActivity({
                action: 'PAGE_VIEW',
                path: location.pathname,
                user_id: auth.user.user_id
            }).catch(err => console.error("Activity logging failed:", err));
        }
    }, [location.pathname, auth.isAuthenticated, auth.user]);


    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const closeCanvas = () => {
        // handlePageAccess();
        document.querySelector('[data-bs-dismiss="offcanvas"]').click();
    }

    const handleLoginClick = () => {
        setShowLoginModal(true); 
    }
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleLogoutClick = () => {
        setAuth({ isAuthenticated: false, loading: false, token: null, user: null });
        navigate('/');
    };


    useEffect(() => {
        setShowSidebar(auth?.user ?? false);
        setShowLoggedOutFeats(!auth?.user);
        if (auth?.user) {
            API.getAwardSettings()
                .then(res => setAwardsOpen(res.data.nominations_enabled || res.data.voting_enabled))
                .catch(() => setAwardsOpen(false));
        } else {
            setAwardsOpen(false);
        }
    }, [auth.user]);

    useEffect(() => {
        // Sync the home icon state with the current path for unauthenticated visitors
        setShowHomeIcon(location.pathname === "/public-square");
    }, [location.pathname]);

    useEffect(() => {
        if (!isAdmin) return;
        const fetchCount = () => {
            if (document.hidden) return;
            API.getAdminFeedbackUnreadCount()
                .then(res => setAdminUnreadCount(res.data.count))
                .catch(() => {});
        };
        fetchCount();
        const interval = setInterval(fetchCount, 0.5 * 60 * 1000);
        const handleVisibility = () => { if (!document.hidden) fetchCount(); };
        document.addEventListener('visibilitychange', handleVisibility);
        const handler = (e) => setAdminUnreadCount(e.detail.count);
        window.addEventListener('feedbackUnreadCount', handler);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('feedbackUnreadCount', handler);
        };
    }, [isAdmin]);

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user) return;
        const fetchCount = () => {
            if (document.hidden) return;
            API.getNotificationUnreadCount(auth.user.user_id)
                .then(res => setNotifUnreadCount(res.data.count))
                .catch(() => {});
        };
        fetchCount();
        const interval = setInterval(fetchCount, 20 * 1000);
        const handleVisibility = () => { if (!document.hidden) fetchCount(); };
        document.addEventListener('visibilitychange', handleVisibility);
        const handler = (e) => setNotifUnreadCount(e.detail.count);
        window.addEventListener('notifUnreadCount', handler);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('notifUnreadCount', handler);
        };
    }, [auth.isAuthenticated, auth.user]);

    // Close notification dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target))
                setShowNotifDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggleNotifDropdown = () => {
        const opening = !showNotifDropdown;
        setShowNotifDropdown(opening);
        if (opening && auth.user) {
            API.getNotifications({ user_id: auth.user.user_id, limit: 10 })
                .then(res => setNotifications(res.data))
                .catch(() => {});
        }
    };

    const handleNotifClick = (notification) => {
        setShowNotifDropdown(false);
        if (!notification.is_read) {
            API.markNotificationRead(notification.id, auth.user.user_id).catch(() => {});
            setNotifUnreadCount(c => Math.max(0, c - 1));
            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
        }
        if (notification.type === "feedback_submitted" && notification.related_id) {
            const [requestId, contactId] = notification.related_id.split("-");
            setFeedbackModal({ requestId, contactId });
            return;
        }
        if (DIGEST_TYPES.includes(notification.type)) {
            setBillDigestModal({ notificationId: notification.id, title: notification.title });
            return;
        }
        if (notification.link) navigate(notification.link);
    };

    const notifTimeAgo = (dateStr) => {
        const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const handlePageAccess = (page) => {
        if (page === "/"){
            navigate("/");
        } else if (!auth.isAuthenticated && page !== "/public-square") {
            alert("Access Restricted. Please login.");
            navigate("/");
        } else {
            navigate(page);
        }
    };

    const handleProfileAccess = async (page) => {
        const profileId = await checkUserProfile();

        if (!auth.isAuthenticated) {
            alert("Access Restricted. Please login.");
            navigate("/");
        } else if (profileId === undefined) {
            navigate("/profile/create");
        } else {
            navigate(page + profileId);
        }
    };

    const handleOffcanvasPageAccess = (page) => {
        closeCanvas();

        if (page === "/"){
            navigate("/");
        } else if (!auth.isAuthenticated && page !== "/public-square") {
            alert("Access Restricted. Please login.");
            navigate("/");
        } else {
            navigate(page);
        }
    };

    const handleOffcanvasProfileAccess = async (page) => {
        closeCanvas();

        const profileId = await checkUserProfile();

        if (!auth.isAuthenticated) {
            alert("Access Restricted. Please login.");
            navigate("/");
        } else if (profileId === undefined) {
            navigate("/profile/create");
        } else {
            navigate(page + profileId);
        }
    };

    const checkUserProfile = async () => {
        try {
            const res = await API.getProfileUserCheck();
            const userProfile = res.data.find(p => p.user_id === auth.user?.user_id);
            return userProfile?.profile_id;
        } catch (error) {
            console.error(error);
        }
    };

    if (isFeedbackPage || isReportPage) return null;

    return (
        <>
            <nav className="top-navbar shadow-sm fixed-top">
                <div className="home-icons">
                    {(showLoggedOutFeats || isConfirmEmailPage) && !showHomeIcon && (
                        <a className="home-icon ms-3" onClick={() => handlePageAccess("/public-square")}><i className="bi bi-newspaper"></i></a>
                    )}
                    {(showLoggedOutFeats || isConfirmEmailPage) && showHomeIcon && (
                        <a className="home-icon ms-3" onClick={() => handlePageAccess("/")}><i className="bi bi-house-fill"></i></a>
                    )}
                </div>
                {!showLoggedOutFeats && !isConfirmEmailPage && isMobileView && (
                    <div className="nav-item ">
                        <button
                            className="nav-link"
                            type="button"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#mobileSidebar"
                            aria-controls="mobileSidebar"
                            aria-label="Toggle navigation"
                        >
                            <i className="bi bi-list"></i>
                        </button>
            
                        <div className="offcanvas offcanvas-start" tabIndex="-1" id="mobileSidebar" aria-labelledby="mobileSidebarLabel">
                            <div className="offcanvas-header">
                                <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                            </div>
                            <div className="offcanvas-body">
                                <ul className="mobile-sidebar-nav mt-0">
                                    {awardsOpen && (
                                        <li className="awards-nav-item" onClick={() => handleOffcanvasPageAccess("/awards")}>
                                            <i className="bi bi-trophy-fill"></i>
                                            <span>Awards</span>
                                        </li>
                                    )}
                                    <li onClick={() => handleOffcanvasPageAccess("/dashboard")}>
                                        <i className="bi bi-kanban"></i>
                                        <span>Dashboard</span>
                                    </li>
                                    <li onClick={() => handleOffcanvasPageAccess("/public-square")}>
                                        <i className="bi bi-newspaper"></i>
                                        <span>Public Square</span>
                                    </li>
                                    <li onClick={() => handleOffcanvasPageAccess("/directory")}>
                                        <i className="bi bi-person-vcard"></i>
                                        <span>Directory</span>
                                    </li>
                                    <li onClick={() => handleOffcanvasPageAccess("/legislation")}>
                                        <i className="bi bi-bank"></i>
                                        <span>Legislation</span>
                                    </li>
                                    <li onClick={() => handleOffcanvasPageAccess("/teams")}>
                                        <i className="bi bi-briefcase"></i>
                                        <span>Workspaces</span>
                                    </li>
                                    <li onClick={() => handleOffcanvasProfileAccess("/profile/")}>
                                        <i className="bi bi-person-circle"></i>
                                        <span>Profile</span>
                                    </li>
                                    {isAmbassador && (
                                        <li onClick={() => handleOffcanvasPageAccess("/ambassador")}>
                                            <i className="bi bi-megaphone"></i>
                                            <span>Ambassador</span>
                                        </li>
                                    )}
                                    {isAdmin && (
                                        <li onClick={() => handleOffcanvasPageAccess("/admin")}>
                                            <span className="sidebar-icon-wrap">
                                                <i className="bi bi-shield-lock"></i>
                                                {adminUnreadCount > 0 && <span className="sidebar-unread-pip" />}
                                            </span>
                                            <span>Admin</span>
                                        </li>
                                    )}
                                </ul>
                                <div className="mobile-sidebar-footer mb-3 ms-4">
                                    <a role="button" data-bs-toggle="modal" data-bs-target="#feedbackModal">
                                        <i className="bi bi-send" />
                                        <span>Feedback</span>
                                    </a>
                                    <a role="button" onClick={() => handleOffcanvasPageAccess("/info")}>
                                        <i className="bi bi-info-circle" />
                                        <span>Info</span>
                                    </a>
                                    <a role="button" onClick={() => { closeCanvas(); handleLogoutClick(); }}>
                                        <i className="bi bi-box-arrow-right" />
                                        <span>Logout</span>
                                    </a>
                                    <p className="sidebar-copyright">&copy; <i>'ello</i>Govna {new Date().getFullYear()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="brand-centered">
                    <a className="govna-brand display-6" onClick={() => handlePageAccess(auth.isAuthenticated ? "/dashboard" : "/public-square")}>
                        <i className="ello-brand">'ello</i><span className="govna-script">Govna</span>
                    </a>
                </div>
                <div className="me-3 account-icons">
                    {contributor && (
                        <span className="eg-tooltip-wrap contributor navbar-icon-spacing">
                            <i className="bi bi-flower2" />
                            <div className="eg-tooltip eg-tooltip--left">
                                <strong>Concierge Account</strong><br/>
                                As a Concierge of 'elloGovna, you're able to contribute to the accuracy of the site and edit unclaimed profiles. Please help update profile info where and when you can.
                            </div>
                        </span>
                    )}
                    {auth.isAuthenticated && (
                        <span className="navbar-icon-wrap position-relative navbar-icon-spacing" ref={notifDropdownRef}>
                            <i className="bi bi-bell" onClick={toggleNotifDropdown}></i>
                            {notifUnreadCount > 0 && <span className="navbar-unread-pip" />}
                            {showNotifDropdown && (
                                <div className="notif-dropdown">
                                    <div className="notif-dropdown-header">Notifications</div>
                                    {notifications.length === 0 ? (
                                        <div className="notif-dropdown-empty">No notifications yet.</div>
                                    ) : (
                                        <ul className="list-unstyled mb-0">
                                            {notifications.map(n => (
                                                <li key={n.id} className={`notif-dropdown-item ${!n.is_read ? 'notif-dropdown-item--unread' : ''}`} onClick={() => handleNotifClick(n)}>
                                                    <div className="notif-dropdown-title">{n.title}</div>
                                                    <div className={`notif-dropdown-message ${DIGEST_TYPES.includes(n.type) ? 'notif-dropdown-message--clamped' : ''}`}>{n.message}</div>
                                                    {DIGEST_TYPES.includes(n.type) && <span className="notif-dropdown-readmore">Read more</span>}
                                                    <div className="notif-dropdown-time">{notifTimeAgo(n.createdAt)}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <a className="notif-dropdown-viewall" onClick={() => { setShowNotifDropdown(false); navigate('/notifications'); }}>View All</a>
                                </div>
                            )}
                        </span>
                    )}
                    {auth.isAuthenticated && !isMobileView && (
                        <a className="user-account" onClick={() => handleProfileAccess("/profile/")}><i className="bi bi-person-circle"></i></a>
                    )}

                    {auth.isAuthenticated ? (
                        !isMobileView && (
                            <a className="account-logout ms-3" onClick={handleLogoutClick}><i className="bi bi-box-arrow-right"></i></a>
                        )
                    ) : (
                        <a className="account-logout ms-3" data-bs-toggle="modal" data-bs-target="#loginModal" onClick={handleLoginClick}><i className="bi bi-box-arrow-in-left"></i></a>
                    )}
                </div>
            </nav>

            <div className="layout-container">
                {showSidebar && !isMobileView && !isConfirmEmailPage && (
                    <div className={`sidebar ${isSidebarOpen ? "expanded" : "collapsed"}`}>
                        <button className="toggle-btn" onClick={toggleSidebar}>
                            <i className="bi bi-list"></i>
                        </button>
                        <ul className="sidebar-nav mt-0">
                            {awardsOpen && (
                                <li className="awards-nav-item" onClick={() => handlePageAccess("/awards")}>
                                    <i className="bi bi-trophy-fill"></i>
                                    {isSidebarOpen && <span>Awards</span>}
                                </li>
                            )}
                            <li onClick={() => handlePageAccess("/dashboard")}>
                                <i className="bi bi-kanban"></i>
                                {isSidebarOpen && <span>Dashboard</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/public-square")}>
                                <i className="bi bi-newspaper"></i>
                                {isSidebarOpen && <span>Public Square</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/directory")}>
                                <i className="bi bi-person-vcard"></i>
                                {isSidebarOpen && <span>Directory</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/legislation")}>
                                <i className="bi bi-bank"></i>
                                {isSidebarOpen && <span>Legislation</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/teams")}>
                                <i className="bi bi-briefcase"></i>
                                {isSidebarOpen && <span>Workspaces</span>}
                            </li>
                            <li onClick={() => handleProfileAccess("/profile/")}>
                                <i className="bi bi-person-circle"></i>
                                {isSidebarOpen && <span>Profile</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/subscribe")}>
                                <i className="bi bi-credit-card"></i>
                                {isSidebarOpen && <span>Subscribe</span>}
                            </li>
                            {isAmbassador && (
                                <li onClick={() => handlePageAccess("/ambassador")}>
                                    <i className="bi bi-megaphone"></i>
                                    {isSidebarOpen && <span>Ambassador</span>}
                                </li>
                            )}
                            {isAdmin && (
                                <li onClick={() => handlePageAccess("/admin")}>
                                    <span className="sidebar-icon-wrap">
                                        <i className="bi bi-shield-lock"></i>
                                        {adminUnreadCount > 0 && <span className="sidebar-unread-pip" />}
                                    </span>
                                    {isSidebarOpen && <span>Admin</span>}
                                </li>
                            )}
                            <li
                                className="sidebar-feedback-item"
                                role="button"
                                data-bs-toggle="modal"
                                data-bs-target="#feedbackModal"
                            >
                                <i className="bi bi-send"></i>
                                {isSidebarOpen && <span>Feedback</span>}
                            </li>
                            <li onClick={() => handlePageAccess("/info")}>
                                <i className="bi bi-info-circle"></i>
                                {isSidebarOpen && <span>Info</span>}
                            </li>
                        </ul>
                        <div className="sidebar-footer">
                            <p className="sidebar-copyright">
                                {isSidebarOpen
                                    ? <>&copy; <i>'ello</i>Govna {new Date().getFullYear()}</>
                                    : <>&copy;</>}
                            </p>
                        </div>
                    </div>
                )}
                
                

                <div className={`main-content ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                    <div className="content-wrapper">
                        <Outlet />
                        {showLoginModal && <LoginModal />}
                    </div>
                </div>
            </div>
            <FeedbackModal />
            {feedbackModal && (
                <FeedbackDetailModal
                    requestId={feedbackModal.requestId}
                    contactId={feedbackModal.contactId}
                    onClose={() => setFeedbackModal(null)}
                />
            )}
            {billDigestModal && (
                <BillDigestModal
                    notificationId={billDigestModal.notificationId}
                    userId={auth.user?.user_id}
                    title={billDigestModal.title}
                    onClose={() => setBillDigestModal(null)}
                />
            )}
            {auth.isAuthenticated && <WorkspaceInviteModal />}
        </>
    );
}